import React, { useCallback, useEffect, useState } from 'react';
import apiService from '../services/api';

interface ActivityUser {
  id?: number;
  first_name?: string;
  last_name?: string;
  email?: string;
}

interface ActivityRow {
  id: number;
  action: string;
  subject_type: string | null;
  subject_id: number | null;
  ip_address: string | null;
  created_at: string;
  user?: ActivityUser | null;
}

interface Paginated {
  data: ActivityRow[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

const ACTION_OPTIONS = [
  { value: '', label: 'كل الإجراءات' },
  { value: 'admin_login', label: 'admin_login' },
  { value: 'admin_logout', label: 'admin_logout' },
  { value: 'user_approve', label: 'user_approve' },
  { value: 'user_block', label: 'user_block' },
  { value: 'service_approve', label: 'service_approve' },
  { value: 'service_reject', label: 'service_reject' },
  { value: 'excel_export', label: 'excel_export' },
];

const ActivityLogPage: React.FC = () => {
  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionFilter, setActionFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const load = useCallback(async (pageNum: number) => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = { page: pageNum, per_page: 20 };
      if (actionFilter) params.action = actionFilter;
      if (fromDate) params.from = fromDate;
      if (toDate) params.to = toDate;

      const res = await apiService.getActivityLogs(params);
      if (!res.success) {
        setError(res.message || 'فشل التحميل');
        setRows([]);
        return;
      }
      const payload = res.data as Paginated;
      const list = Array.isArray(payload?.data) ? payload.data : [];
      setRows(list);
      setPage(payload?.current_page ?? pageNum);
      setLastPage(payload?.last_page ?? 1);
      setTotal(payload?.total ?? list.length);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'خطأ غير متوقع');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [actionFilter, fromDate, toDate]);

  useEffect(() => {
    void load(1);
    // Initial load only; use «تطبيق» to re-fetch after changing filters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const adminName = (u: ActivityUser | null | undefined) => {
    if (!u) return '—';
    const n = `${u.first_name || ''} ${u.last_name || ''}`.trim();
    return n || u.email || `#${u.id}`;
  };

  const subjectLabel = (r: ActivityRow) => {
    if (r.subject_type && r.subject_id) return `${r.subject_type} #${r.subject_id}`;
    return '—';
  };

  return (
    <div className="p-6 max-w-[1200px] mx-auto space-y-6" dir="rtl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">سجل النشاط</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          عرض أحداث لوحة التحكم (بدون تحليلات).
        </p>
      </div>

      <div className="flex flex-wrap gap-4 items-end bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
        <div>
          <label className="block text-xs text-gray-500 mb-1">نوع الإجراء</label>
          <select
            className="border rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:text-white dark:border-gray-600"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
          >
            {ACTION_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">من تاريخ</label>
          <input
            type="date"
            className="border rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:text-white dark:border-gray-600"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">إلى تاريخ</label>
          <input
            type="date"
            className="border rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:text-white dark:border-gray-600"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
        <button
          type="button"
          onClick={() => load(1)}
          className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
        >
          تطبيق
        </button>
      </div>

      {error && (
        <div className="text-red-600 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-md">{error}</div>
      )}

      <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50">
              <th className="text-right p-3 font-semibold text-gray-700 dark:text-gray-300">الوقت</th>
              <th className="text-right p-3 font-semibold text-gray-700 dark:text-gray-300">المسؤول</th>
              <th className="text-right p-3 font-semibold text-gray-700 dark:text-gray-300">الإجراء</th>
              <th className="text-right p-3 font-semibold text-gray-700 dark:text-gray-300">الموضوع</th>
              <th className="text-right p-3 font-semibold text-gray-700 dark:text-gray-300">IP</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="p-8 text-center text-gray-500">
                  جاري التحميل...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-8 text-center text-gray-500">
                  لا توجد سجلات
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className="border-b border-gray-100 dark:border-gray-700/80 hover:bg-gray-50/80 dark:hover:bg-gray-900/30">
                  <td className="p-3 text-gray-800 dark:text-gray-200 whitespace-nowrap">
                    {r.created_at ? new Date(r.created_at).toLocaleString('ar-SA') : '—'}
                  </td>
                  <td className="p-3 text-gray-800 dark:text-gray-200">{adminName(r.user)}</td>
                  <td className="p-3 font-mono text-xs text-gray-900 dark:text-gray-100">{r.action}</td>
                  <td className="p-3 text-gray-700 dark:text-gray-300 text-xs">{subjectLabel(r)}</td>
                  <td className="p-3 font-mono text-xs text-gray-600 dark:text-gray-400">{r.ip_address || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!loading && lastPage > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <span>
            الصفحة {page} من {lastPage} — إجمالي {total}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => load(page - 1)}
              className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40"
            >
              السابق
            </button>
            <button
              type="button"
              disabled={page >= lastPage}
              onClick={() => load(page + 1)}
              className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40"
            >
              التالي
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityLogPage;
